/**
 * KEY-751: whether to offer "add the people on this dial", and remembering a "Not now".
 *
 * A dial whose fields pick a person (an appointment's "Who it's for") is no use until Who's who has someone in it,
 * and a new user had no way to know that lived under the dial's settings. So a dial admin is asked once, on a
 * dial that has a Person field and nobody in Who's who. Not compulsory: it is a card above the dial, not the
 * first-run capture screen. Adding anyone puts it away for good (the condition stops holding); "Not now" puts it
 * away for this browser.
 *
 * Every storage access is wrapped: a blocked or full store must never take the workspace down.
 */
import { isPersonAttributeType, type InstanceDetail } from "@keydials/api-client";

export const ADD_PEOPLE_DISMISSED_KEY_PREFIX = "kde.add-people-prompt-dismissed.v1.";

type PromptStorage = Pick<Storage, "getItem" | "setItem">;

const keyFor = (instanceId: string) => `${ADD_PEOPLE_DISMISSED_KEY_PREFIX}${instanceId}`;

function defaultStorage(): PromptStorage | null {
  try {
    return localStorage;
  } catch {
    return null;
  }
}

/**
 * True when the dial uses Who's who and has nobody in it, and the viewer is the one who can fix that.
 * `peopleEnabled` matters because `detail.people` is also absent when the feature is switched off.
 */
export function shouldOfferAddPeople(
  detail: Pick<InstanceDetail, "people" | "nodeAttributes" | "isGroup" | "isTesting">,
  gates: { peopleEnabled: boolean; isDialAdmin: boolean },
): boolean {
  if (!gates.peopleEnabled || !gates.isDialAdmin || detail.isGroup || detail.isTesting) return false;
  const usesPeople = (detail.nodeAttributes ?? []).some((a) => isPersonAttributeType(a.type));
  return usesPeople && (detail.people ?? []).length === 0;
}

export function isAddPeopleDismissed(instanceId: string, storage: PromptStorage | null = defaultStorage()): boolean {
  try {
    return storage?.getItem(keyFor(instanceId)) === "1";
  } catch {
    return false;
  }
}

export function dismissAddPeople(instanceId: string, storage: PromptStorage | null = defaultStorage()): void {
  try {
    storage?.setItem(keyFor(instanceId), "1");
  } catch {
    // Storage full, blocked, or disabled. The prompt returns next load; a crash would be worse.
  }
}
